import { useCallback } from 'react';

import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { AutoscalerFeatures } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const AUTOSCALER_FEATURES_QUERY_KEY = 'autoscaler-features';

const getAutoscalerFeaturesQueryKey = (id: string) =>
  [AUTOSCALER_FEATURES_QUERY_KEY, id] as const;

const getAutoscalerFeaturesQueryOptions = (
  id: string,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  queryOptions({
    queryKey: getAutoscalerFeaturesQueryKey(id),
    queryFn: async () => {
      const { data } = await apiClient.autoscaler.getAutoscalerFeatures({
        clusterId: id,
      });
      return data;
    },
    enabled: !!id && enabled,
  });

type UseAutoscalerFeaturesQuery = {
  isLoading: boolean;
  isSuccess: boolean;
  features?: AutoscalerFeatures;
  error: unknown;
  refetch: () => void;
  prefetch: () => void;
};

type Options = {
  enabled?: boolean;
};

export const useAutoscalerFeaturesQuery = (
  id: string,
  { enabled = true }: Options = {}
): UseAutoscalerFeaturesQuery => {
  const queryClient = useQueryClient();

  const {
    isLoading,
    data: features,
    error,
    refetch,
    isSuccess,
  } = useQuery(getAutoscalerFeaturesQueryOptions(id, { enabled }));

  const prefetch = useCallback(() => {
    if (id) {
      queryClient.prefetchQuery(getAutoscalerFeaturesQueryOptions(id));
    }
  }, [id, queryClient]);

  return {
    isSuccess,
    isLoading,
    features,
    error,
    refetch,
    prefetch,
  };
};
